
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {Tick} from '../core/Tick';
import {FieldModelTick} from '../core/Tick';

/**
 * TickGetResponse struct
 */
class TickGetResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!UUID=} symbolId
   * @param {!Tick=} tick
   * @constructor
   */
  constructor (parent = new Response(), symbolId = new UUID(), tick = new Tick()) {
    super()
    super.copy(parent)
    this.SymbolId = symbolId
    this.Tick = tick
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TickGetResponse}
   * @param {!TickGetResponse} other Other struct
   * @returns {!TickGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SymbolId != null) {
      this.SymbolId = new UUID(other.SymbolId)
    } else {
      this.SymbolId = null
    }
    if (other.Tick != null) {
      this.Tick = Tick.fromObject(other.Tick)
    } else {
      this.Tick = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TickGetResponse}
   * @returns {!TickGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TickGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TickGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TickGetResponse}
   * @param {!TickGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TickGetResponse)) {
      throw new TypeError('Instance of TickGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TickGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SymbolId: ((this.SymbolId != null) ? this.SymbolId.toString() : null),
      Tick: ((this.Tick != null) ? this.Tick : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TickGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TickGetResponse} other Object value
   * @returns {!TickGetResponse} Created struct
   */
  static fromObject (other) {
    return new TickGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TickGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TickGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TickGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 2025
  }
}

export { TickGetResponse };

/**
 * Fast Binary Encoding TickGetResponse field model
 */
class FieldModelTickGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.SymbolId = new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Tick = new FieldModelTick(buffer, this.SymbolId.fbeOffset + this.SymbolId.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelTickGetResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SymbolId.fbeSize + this.Tick.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SymbolId.fbeExtra + this.Tick.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTickGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 2025
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTickGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTickGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.Tick.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Tick.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Tick.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTickGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTickGetResponse}
   * @param {!TickGetResponse} fbeValue Default value, defaults is new TickGetResponse()
   * @returns {!TickGetResponse} TickGetResponse value
   */
  get (fbeValue = new TickGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTickGetResponse}
   * @param {!TickGetResponse} fbeValue TickGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolId = this.SymbolId.get()
    } else {
      fbeValue.SymbolId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.Tick.fbeSize) <= fbeStructSize) {
      fbeValue.Tick = this.Tick.get()
    } else {
      fbeValue.Tick = new Tick()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Tick.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTickGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTickGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTickGetResponse}
   * @param {!TickGetResponse} fbeValue TickGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTickGetResponse}
   * @param {TickGetResponse} fbeValue TickGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SymbolId.set(fbeValue.SymbolId)
    this.Tick.set(fbeValue.Tick)
  }
}

export { FieldModelTickGetResponse };

/**
 * Fast Binary Encoding TickGetResponse model
 */
class TickGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTickGetResponse(this.buffer, 4)
  }

  /**
   * Get the TickGetResponse model
   * @this {!TickGetResponseModel}
   * @returns {!FieldModelTickGetResponse} model TickGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TickGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TickGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TickGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TickGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTickGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TickGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TickGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TickGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TickGetResponseModel}
   * @param {!TickGetResponse} value TickGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TickGetResponseModel}
   * @param {!TickGetResponse} value TickGetResponse value, defaults is new TickGetResponse()
   * @return {!object} Deserialized TickGetResponse value and its size
   */
  deserialize (value = new TickGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TickGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TickGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TickGetResponseModel}
   * @param {!number} prev Previous TickGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TickGetResponseModel };
